/**
 * Auth utility functions.
 *
 * - Cache utilities for checking auth state
 * - Strategy store utilities for common patterns (operation ID, anonymous fallback)
 */

import type { Result } from "true-myth/result";

import type { AuthCache } from "./cache";
import type { AuthError, AuthMode, FrontendUser } from "./types";
import { useAuthStore } from "./store";

// ─────────────────────────────────────────────────────────────────────────────
// Cache Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if user is authenticated (has any session, including anonymous).
 *
 * Use for: Route guards that require any session.
 */
export function isAuthenticated(cache: AuthCache): boolean {
  return cache.getUser() != null;
}

/**
 * Check if user is logged in (authenticated AND not anonymous).
 *
 * Anonymous users are "authenticated" but not "logged in".
 * Use for: UI that shows "Sign In" vs "Profile" buttons.
 */
export function isLoggedIn(cache: AuthCache): boolean {
  const user = cache.getUser();
  return user != null && !user.isAnonymous;
}

// ─────────────────────────────────────────────────────────────────────────────
// Strategy Store Types
// ─────────────────────────────────────────────────────────────────────────────

/** Result of creating an anonymous session */
export interface AnonymousResult {
  user: FrontendUser;
  token?: string;
  expiresAt?: number;
}

/** Store accessor for operation ID management */
export interface OperationIdAccessor {
  getOperationId: () => number;
  setOperationId: (id: number) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Operation ID Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Start a new operation and return its ID.
 *
 * Used for race condition protection - operations check if their ID
 * is still current before applying results.
 */
export function startOperation(accessor: OperationIdAccessor): number {
  const opId = accessor.getOperationId() + 1;
  accessor.setOperationId(opId);
  return opId;
}

/**
 * Check if an operation is stale (superseded by a newer operation).
 *
 * Returns true if the operation should be abandoned.
 */
export function isStaleOperation(
  opId: number,
  accessor: OperationIdAccessor
): boolean {
  return accessor.getOperationId() !== opId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Anonymous Session Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create an anonymous session and set auth state.
 *
 * Shared by both cookie and PKCE stores for the "guaranteed user" pattern.
 * Handles operation staleness checks automatically.
 *
 * @param createAnonymous - Strategy-specific function to create anonymous session
 * @param mode - Auth mode ("cookie" or "bearer")
 * @param opId - Current operation ID
 * @param accessor - Operation ID accessor for staleness checks
 * @param onSuccess - Optional callback when anonymous session is created (e.g., to update cache)
 */
// biome-ignore lint/nursery/useMaxParams: Internal utility with clear sequential parameters
export async function createAnonymousAndSetAuth(
  createAnonymous: () => Promise<Result<AnonymousResult, AuthError>>,
  mode: AuthMode,
  opId: number,
  accessor: OperationIdAccessor,
  onSuccess?: (user: FrontendUser) => void
): Promise<void> {
  if (isStaleOperation(opId, accessor)) {
    return;
  }

  const result = await createAnonymous();

  if (isStaleOperation(opId, accessor)) {
    return;
  }

  if (result.isOk) {
    onSuccess?.(result.value.user);
    useAuthStore
      .getState()
      .setAuth(
        result.value.user,
        mode,
        result.value.token,
        result.value.expiresAt
      );
  } else {
    useAuthStore.getState().clearAuth(result.error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SignOut Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sign out and create a new anonymous session.
 *
 * Shared pattern for the "guaranteed user" model where signing out
 * transitions to an anonymous user rather than null.
 *
 * Steps:
 * 1. Emit signed_out event (before clearing state)
 * 2. Call strategy-specific signOut
 * 3. Create anonymous session
 * 4. Set auth state
 *
 * @param signOutFn - Strategy-specific signOut function
 * @param createAnonymous - Strategy-specific anonymous creation function
 * @param mode - Auth mode ("cookie" or "bearer")
 * @param opId - Current operation ID
 * @param accessor - Operation ID accessor for staleness checks
 * @param onClear - Optional callback to clear strategy-specific state (e.g., cache)
 * @param onSuccess - Optional callback when anonymous session is created
 */
// biome-ignore lint/nursery/useMaxParams: Internal utility with clear sequential parameters
export async function signOutWithAnonymousFallback(
  signOutFn: () => Promise<void>,
  createAnonymous: () => Promise<Result<AnonymousResult, AuthError>>,
  mode: AuthMode,
  opId: number,
  accessor: OperationIdAccessor,
  onClear?: () => void,
  onSuccess?: (user: FrontendUser) => void
): Promise<void> {
  // Emit before clearing (so handlers can access current state)
  useAuthStore.getState().emitSignedOut();

  await signOutFn();

  if (isStaleOperation(opId, accessor)) {
    return;
  }

  onClear?.();

  await createAnonymousAndSetAuth(
    createAnonymous,
    mode,
    opId,
    accessor,
    onSuccess
  );
}
